/**
 * Booking Service
 * 
 * Handles booking creation and management via the internal API.
 * 
 * ## Key Features
 * - Type-safe discriminated unions (ApiResult<T>)
 * - Automatic error handling via apiClient
 * - Tenant context injection via appRuntime
 * - Payment processing during booking creation
 * 
 * @module booking/api
 */

import { apiClient } from '~/lib/apiClient';
import { appRuntime } from '~/services/appRuntime';
import { API_CONFIG } from '~/lib/apiClient/constants';
import { getAccessToken } from '~/services/auth/storage/tokenStorage';
import type { ApiResult } from '~/lib/apiClient/types';
import type { CreateBookingPayload, ApiBookingWithRelations, ApiCreateBookingResponse, ApiValidateBookingResponse } from './types';
import type { ApiBookingQuoteResponse } from '@lgc/database/types/domains/booking';
import { recordQuotedFees } from '~/features/dev-tools/rateBreakdown';
import type { ApiCancelBookingPayload, ApiExtendBookingRequest, ApiExtendBookingResponse, ApiExtensionOfferResponse } from '@lgc/database/types/domains/booking';

/**
 * Booking Service
 * 
 * Handles all booking-related API operations
 */
export const bookingService = {
  /**
   * Create a new booking with payment
   * 
   * The API handles:
   * - Booking validation (schedule, conflicts, subscription limits)
   * - Payment processing (charges the payment method token)
   * - Invoice creation
   * - Access code generation
   * 
   * **IMPORTANT**: Does NOT save payment method to database.
   * Use `paymentMethodService.savePaymentMethod()` separately to store
   * the token for future bookings.
   * 
   * @param payload - Booking details with payment token
   * @returns ApiResult with created booking data
   * 
   * @example
   * ```typescript
   * // Walk-in booking with payment
   * const result = await bookingService.createBooking({
   *   clubId: 1,
   *   bayId: 2,
   *   userId: 123,
   *   startTime: '2025-12-16T10:00:00.000Z',
   *   endTime: '2025-12-16T11:00:00.000Z',
   *   paymentMethodId: 'cnon:xxx', // Square nonce
   *   paymentAmount: 50.00
   * });
   * 
   * if (result.success) {
   *   console.log('Booking created:', result.payload);
   * } else {
   *   console.error('Error:', result.message);
   * }
   * ```
   * 
   * @example
   * ```typescript
   * // Membership booking with overage
   * const result = await bookingService.createBooking({
   *   clubId: 1,
   *   bayId: 2,
   *   userId: 123,
   *   startTime: '2025-12-16T10:00:00.000Z',
   *   endTime: '2025-12-16T12:00:00.000Z',
   *   membershipId: 456,
   *   paymentAmount: 0, // Membership covers base time
   *   hasOveragePayment: true,
   *   overagePayment: {
   *     overageMinutes: 30,
   *     overageAmount: 25.00,
   *     paymentMethodId: 'cnon:xxx'
   *   }
   * });
   * ```
   */
  async createBooking(
    payload: CreateBookingPayload
  ): Promise<ApiResult<ApiCreateBookingResponse>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;

    return apiClient.post<ApiCreateBookingResponse>(
      '/bookings',
      payload,
      {
        tenantHandle: tenantHandle || undefined,
        clubId: clubId ? parseInt(clubId) : undefined,
        // `retries: 0`: creating a booking charges a card, and there is no
        // cross-request idempotency behind this endpoint. apiClient still retries
        // THROWN ApiErrors — a PARSE_ERROR carrying a 502 from a rolling deploy
        // qualifies — so a transient failure after a successful charge would
        // otherwise double-book and double-charge.
        retries: 0,
      }
    );
  },

  /**
   * Get booking by ID
   * 
   * Retrieves detailed booking information including bay, club, and payment details.
   * 
   * **IMPORTANT**: This requires authentication. The booking must belong to the
   * authenticated user (authorization check happens on backend).
   * 
   * @param bookingId - Booking ID
   * @returns ApiResult with booking data
   * 
   * @example
   * ```typescript
   * const result = await bookingService.getBookingById(184);
   * 
   * if (result.success) {
   *   console.log('Booking:', result.payload);
   *   console.log('Bay:', result.payload.bay);
   *   console.log('Access Code:', result.payload.accessCodeValue);
   * } else {
   *   console.error('Error:', result.message);
   * }
   * ```
   */
  async getBookingById(
    bookingId: number
  ): Promise<ApiResult<ApiBookingWithRelations>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;

    // `?include=bay,club` opts into the relations this page renders (the API default
    // is `bay` only). FOLLOW-UP: timezone could instead come from the already-cached
    // `useClubQuery()` (ClubDataPrefetcher loads it), letting us drop `club` here.
    return apiClient.get<ApiBookingWithRelations>(
      `/bookings/${bookingId}?include=bay,club`,
      {
        tenantHandle: tenantHandle || undefined,
        clubId: clubId ? parseInt(clubId) : undefined,
      }
    );
  },

  /**
   * Price a booking with no account — the guest quote.
   *
   * `POST /bookings/quote`, tenant-only. Deliberately takes NO userId: the
   * endpoint is unauthenticated and ignores one if sent, so offering the field
   * here would only invite a caller to believe it does something.
   *
   * Use this when `isAuthenticated` is false; `validateBooking` otherwise. The
   * two return different payloads — this one carries no usage, subscription or
   * overage, because an anonymous caller holds no plan.
   */
  async quoteBooking(
    payload: {
      clubId: number;
      bayId: number;
      startTime: string;
      endTime: string;
      guestCount?: number;
      /** Priced loosely — the guest route has no identity to check against. */
      promoCode?: string;
    }
  ): Promise<ApiResult<ApiBookingQuoteResponse['payload']>> {
    const result = await apiClient.post<ApiBookingQuoteResponse['payload']>('/bookings/quote', payload, {
      // No bearer exists on the guest path; asking for one logs a dev warning
      // on every keystroke-driven re-quote.
      skipAuth: true,
    });
    // Dev-tools only, and a no-op otherwise: keeps the last quote so the
    // Pricing Breakdown panel has something to render without re-quoting.
    if (result.success) recordQuotedFees(result.payload?.fees);
    return result;
  },

  /**
   * Validate booking with subscription limits
   * 
   * Checks if a booking is valid and calculates any overage charges
   * based on user's active subscription (membership or pass).
   * 
   * Returns validation status including:
   * - Whether booking is allowed
   * - Subscription coverage information
   * - Overage charges if limits exceeded
   * - Error messages if validation fails
   * 
   * @param payload - Validation request with booking details
   * @returns ApiResult with validation response
   * 
   * @example
   * ```typescript
   * const result = await bookingService.validateBooking({
   *   clubId: 1,
   *   bayId: 2,
   *   startTime: '2025-12-22T10:00:00.000Z',
   *   endTime: '2025-12-22T11:00:00.000Z',
   *   userId: 123
   * });
   * 
   * if (result.success) {
   *   const validation = result.payload;
   *   console.log('Is valid:', validation.isValid);
   *   console.log('Has overage:', validation.overage?.hasOverage);
   *   console.log('Overage cost:', validation.overageCost?.amount);
   * }
   * ```
   */
  async validateBooking(
    payload: {
      clubId: number;
      bayId: number;
      startTime: string;
      endTime: string;
      userId?: number;
      guestCount?: number;
      /** Priced strictly — this path proves an identity. */
      promoCode?: string;
    }
  ): Promise<ApiResult<ApiValidateBookingResponse['payload']>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;

    const result = await apiClient.post<ApiValidateBookingResponse['payload']>(
      '/bookings/validate',
      payload,
      {
        tenantHandle: tenantHandle || undefined,
        clubId: clubId ? parseInt(clubId) : undefined,
      }
    );
    if (result.success) recordQuotedFees(result.payload?.fees);
    return result;
  },

  /**
   * Cancel a booking
   * 
   * Cancels a booking and processes refunds automatically based on payment type:
   * - **Pass bookings**: Minutes restored automatically
   * - **Membership bookings**: No refund needed (billing based on non-cancelled bookings)
   * - **Direct payment bookings**: User CANNOT cancel (must contact club) - returns 403 error
   * - **Hybrid bookings** (with overage): User CANNOT cancel (must contact club) - returns 403 error
   * 
   * The API handles:
   * - Status validation (only pending/confirmed can be cancelled)
   * - Cancellation window validation
   * - Automatic pass minute refunds
   * - Access control revocation
   * - Cancellation email notification
   * 
   * **Money → credit:** any real money paid (direct / hybrid / cross-club) is
   * converted to ACCOUNT CREDIT — the card is never charged or refunded.
   * `payload.creditedAmountCents` is the amount credited (0 for pass/membership).
   * Pass/membership bookings restore their allowance.
   *
   * **Remaining error code:** `EXPIRED_PASS_CANCELLATION` — an expired-pass booking
   * still routes to the club (nothing to restore or credit).
   *
   * @param bookingId - Booking ID to cancel
   * @returns ApiResult with `{ bookingId, creditedAmountCents }`
   *
   * @example
   * ```typescript
   * const result = await bookingService.cancelBooking(184);
   * if (result.success && result.payload.creditedAmountCents > 0) {
   *   butterToast.success(`Credited $${(result.payload.creditedAmountCents / 100).toFixed(2)}`);
   * }
   * ```
   */
  async cancelBooking(
    bookingId: number
  ): Promise<ApiResult<ApiCancelBookingPayload>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;

    return apiClient.patch<ApiCancelBookingPayload>(
      `/bookings/${bookingId}/cancel`,
      undefined, // No body needed
      {
        tenantHandle: tenantHandle || undefined,
        clubId: clubId ? parseInt(clubId) : undefined,
      }
    );
  },

  /**
   * One more block on a booking in progress, priced for this member — or why there is
   * none (Simulator Companion slice 9). `offer: null` is a 200: the row simply hides.
   */
  async getExtensionOffer(bookingId: number): Promise<ApiResult<ApiExtensionOfferResponse>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;
    return apiClient.get<ApiExtensionOfferResponse>(`/bookings/${bookingId}/extension-offer`, {
      tenantHandle: tenantHandle || undefined,
      clubId: clubId ? parseInt(clubId) : undefined,
    });
  },

  /**
   * Add one block to a booking in progress and pay for it. `expectedEndTime` is the end
   * the member saw: a booking extended since answers 409 with its current end.
   */
  async extendBooking(bookingId: number, body: ApiExtendBookingRequest): Promise<ApiResult<ApiExtendBookingResponse>> {
    const tenantHandle = appRuntime.tenantHandle;
    const clubId = appRuntime.clubId;
    return apiClient.post<ApiExtendBookingResponse>(`/bookings/${bookingId}/extend`, body, {
      tenantHandle: tenantHandle || undefined,
      clubId: clubId ? parseInt(clubId) : undefined,
    });
  },

  /**
   * Download a booking receipt as PDF
   * 
   * The API returns the PDF in a standardized JSON response with base64-encoded data:
   * ```
   * {
   *   success: true,
   *   payload: {
   *     filename: "receipt-booking-123.pdf",
   *     contentType: "application/pdf",
   *     size: 12345,
   *     data: "<base64-encoded-pdf>"
   *   },
   *   message: "Receipt generated successfully"
   * }
   * ```
   * 
   * This method decodes the base64 data and returns it as a Blob for download.
   * 
   * @param bookingId - ID of the booking to download receipt for
   * @returns ApiResult with blob data or error
   * 
   * @example
   * ```typescript
   * const result = await bookingService.downloadReceipt(184);
   * 
   * if (result.success) {
   *   // Create download link
   *   const url = window.URL.createObjectURL(result.payload);
   *   const a = document.createElement('a');
   *   a.href = url;
   *   a.download = `receipt-booking-${bookingId}.pdf`;
   *   a.click();
   *   window.URL.revokeObjectURL(url);
   * } else {
   *   console.error('Download failed:', result.message);
   * }
   * ```
   */
  async downloadReceipt(
    bookingId: number
  ): Promise<ApiResult<Blob>> {
    try {
      // Get authentication token
      const token = await getAccessToken();
      
      if (!token) {
        return {
          httpOk: false,
          httpStatus: 401,
          success: false,
          message: 'Authentication required. Please log in again.',
          error: {
            code: 'UNAUTHENTICATED',
          },
        };
      }

      // Construct API URL following internal API pattern
      const apiUrl = `${API_CONFIG.baseUrl}${API_CONFIG.version}/bookings/${bookingId}/receipt`;
      
      // Build headers with tenant context (following apiClient pattern)
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Add tenant context if available
      if (appRuntime.tenantHandle) {
        headers['X-Tenant-ID'] = appRuntime.tenantHandle;
      }

      if (appRuntime.clubId) {
        headers['X-Club-ID'] = appRuntime.clubId;
      }

      // Make request
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      // Parse JSON response (API now uses standardized format)
      const jsonData = await response.json();
      
      if (!response.ok || !jsonData.success) {
        // Spread the standardized error response from backend
        return {
          httpOk: response.ok,
          httpStatus: response.status,
          ...jsonData, // success, message, error (all from backend)
        };
      }

      // Extract base64 PDF data from standardized response
      const { data: base64Data, contentType } = jsonData.payload;
      
      if (!base64Data) {
        return {
          httpOk: false,
          httpStatus: 500,
          success: false,
          message: 'Invalid response: missing PDF data',
          error: {
            code: 'INVALID_RESPONSE',
          },
        };
      }

      // Decode base64 to binary
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Create blob with proper MIME type
      const blob = new Blob([bytes], { type: contentType || 'application/pdf' });
      
      // Spread the standardized success response from backend
      return {
        httpOk: true,
        httpStatus: 200,
        ...jsonData, // success, message, meta (all from backend)
        payload: blob, // Replace base64 payload with decoded Blob
      };
    } catch (error) {
      console.error('[bookingService.downloadReceipt] Error:', error);
      
      return {
        httpOk: false,
        httpStatus: 500,
        success: false,
        message: error instanceof Error ? error.message : 'Failed to download receipt',
        error: {
          code: 'DOWNLOAD_ERROR',
        },
      };
    }
  },
};
